/**
 * Utility functions for handling weather API requests to Open-Meteo
 * No API key required!
 */

// Open-Meteo API base URL
const OPEN_METEO_API_BASE_URL = 'https://api.open-meteo.com/v1/forecast';

// Weather data cache with expiration time (1 hour = 3600000 ms)
const weatherCache = {
  data: {},
  // Get data from cache if available and not expired
  get: function(key) {
    const cacheEntry = this.data[key];
    if (!cacheEntry) return null;
    
    const now = Date.now();
    if (now - cacheEntry.timestamp > 3600000) {
      // Cache entry is older than 1 hour, remove it
      delete this.data[key];
      return null;
    }
    
    return cacheEntry.data;
  },
  // Store data in cache with current timestamp
  set: function(key, data) {
    this.data[key] = {
      data,
      timestamp: Date.now()
    };
  }
};

/**
 * Fetch weather data for a specific location from Open-Meteo API
 * Uses a cache to avoid unnecessary API calls
 * 
 * @param {Object} params Request parameters
 * @param {string} params.city City name with optional country
 * @param {number} params.days Number of days for forecast (default: 4)
 * @param {boolean} params.forceRefresh Force refresh the data even if cached
 * @returns {Promise<Object>} Weather data response
 */
export const fetchWeatherData = async (params) => {
  try {
    const { city, days = 4, forceRefresh = false } = params;
    
    // Create cache key
    const cacheKey = `weather_${city}_${days}`;
    
    // Check cache if we're not forcing a refresh
    if (!forceRefresh) {
      const cachedData = weatherCache.get(cacheKey);
      if (cachedData) {
        console.log(`Using cached weather data for ${city}`);
        return cachedData;
      }
    }
    
    // Parse city and country
    const [cityName, countryName] = city.split(',').map(part => part.trim());
    
    // First, use geocoding to get coordinates for the city
    const geoCoords = await geocodeCity(cityName, countryName);
    
    // Build the request URL for Open-Meteo
    const url = new URL(OPEN_METEO_API_BASE_URL);
    url.searchParams.append('latitude', geoCoords.latitude.toString());
    url.searchParams.append('longitude', geoCoords.longitude.toString());
    url.searchParams.append('daily', 'temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode,windspeed_10m_max,relative_humidity_2m_max');
    url.searchParams.append('timezone', 'auto');
    url.searchParams.append('forecast_days', days.toString());
    
    // Make the API request
    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`Weather API returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Transform the data into a more convenient format
    const transformedData = transformWeatherData(data, city);
    
    // Store in cache
    weatherCache.set(cacheKey, transformedData);
    
    return transformedData;
  } catch (error) {
    console.error('Error fetching weather data:', error);
    throw error;
  }
};

/**
 * Clear weather cache for a specific city or all cities
 * 
 * @param {string} city Optional city name to clear specific cache
 */
export const clearWeatherCache = (city = null) => {
  if (city) {
    // Clear cache for a specific city
    Object.keys(weatherCache.data).forEach(key => {
      if (key.includes(`weather_${city}`)) {
        delete weatherCache.data[key];
      }
    });
  } else {
    // Clear all weather cache
    weatherCache.data = {};
  }
};

/**
 * Geocode a city name to get latitude and longitude
 * Uses a free geocoding service
 * 
 * @param {string} city City name
 * @param {string} country Country name
 * @returns {Promise<Object>} Coordinates {latitude, longitude}
 */
async function geocodeCity(city, country) {
  try {
    // Use OpenStreetMap's Nominatim service for geocoding (free and doesn't require API key)
    const query = `${city}${country ? `, ${country}` : ''}`;
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'trip147-Travel-App' // Required by Nominatim
      }
    });
    
    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data || data.length === 0) {
      throw new Error(`No coordinates found for ${query}`);
    }
    
    // Return the first result's coordinates
    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon),
      displayName: data[0].display_name
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
}

/**
 * Transform the raw Open-Meteo data into a more convenient format
 * 
 * @param {Object} data Raw API response
 * @param {string} city City name
 * @returns {Object} Transformed weather data
 */
function transformWeatherData(data, city) {
  try {
    // Extract the daily forecast data
    const { daily } = data;
    
    // Format the forecast data
    const forecastData = daily.time.map((date, index) => {
      return {
        date,
        tempMax: Math.round(daily.temperature_2m_max[index]),
        tempMin: Math.round(daily.temperature_2m_min[index]),
        condition: getWeatherCondition(daily.weathercode[index]),
        precipitation: daily.precipitation_sum[index],
        humidity: daily.relative_humidity_2m_max ? daily.relative_humidity_2m_max[index] : null,
        windSpeed: daily.windspeed_10m_max ? Math.round(daily.windspeed_10m_max[index]) : null
      };
    });
    
    return {
      city,
      forecast: forecastData,
      timezone: data.timezone || null,
      timezoneAbbreviation: data.timezone_abbreviation || null,
      utcOffsetSeconds: data.utc_offset_seconds || null,
      units: {
        temperature: 'C',
        precipitation: 'mm',
        windSpeed: 'km/h'
      }
    };
  } catch (error) {
    console.error('Error transforming weather data:', error);
    throw new Error('Failed to process weather data');
  }
}

/**
 * Determine the weather condition based on Open-Meteo weather codes
 * Using WMO Weather interpretation codes: https://open-meteo.com/en/docs
 * 
 * @param {number} code Weather code from Open-Meteo API
 * @returns {string} Weather condition description
 */
function getWeatherCondition(code) {
  switch (true) {
    case code === 0:
      return 'Clear sky';
    case code === 1:
      return 'Mainly clear';
    case code === 2:
      return 'Partly cloudy';
    case code === 3:
      return 'Overcast';
    case [45, 48].includes(code):
      return 'Fog';
    case [51, 53, 55].includes(code):
      return 'Drizzle';
    case [56, 57].includes(code):
      return 'Freezing drizzle';
    case [61, 63, 65].includes(code):
      return 'Rain';
    case [66, 67].includes(code):
      return 'Freezing rain';
    case [71, 73, 75].includes(code):
      return 'Snow';
    case code === 77:
      return 'Snow grains';
    case [80, 81, 82].includes(code):
      return 'Rain showers';
    case [85, 86].includes(code):
      return 'Snow showers';
    case code === 95:
      return 'Thunderstorm';
    case [96, 99].includes(code):
      return 'Thunderstorm with hail';
    default:
      return 'Unknown';
  }
} 